import React, { useRef, useEffect, useState } from "react";
import useApp from "hooks/useApp";
import useApi from "hooks/useApi";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import GridComponents from "./Grid.Components";
import GridMethods from "./Grid.Methods";
import CircularWait from "components/Fragments/CircularWait";
import "./GHE.css";

export default function GHE() {
    const api = useApi();
    const gridComponent = new GridComponents();
    const gridMethods = new GridMethods();

    const dt = useRef(null);

    const { empid } = useApp();
    const [SelectedGroup, setSelectedGroup] = useState(null);
    const [GroupList, setGroupList] = useState([]);
    const [ListGridProducts, setListGridProducts] = useState(null);
    const [SelectedProducts, setSelectedProducts] = useState(null);
    const [ProdutosList, setProdutosList] = useState([]);
    const [EditingRows, setEditingRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");

    // Estado do Modal de Inclusão de Produto
    const [isAddProductDialogVisible, setIsAddProductDialogVisible] = useState(false);
    const [newProductData, setNewProductData] = useState({
        prodid: null,
        qtde: 1,
        periodic: 90
    });
    const [savingProduct, setSavingProduct] = useState(false);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const data = await gridMethods.fetchDataGrupsList();
            setGroupList(data || []);
        } catch (error) {
            console.error("Erro ao buscar grupos:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRefreshGroups = async () => {
        await fetchGroups();
        if (SelectedGroup) {
            await reloadProductsForGroup(SelectedGroup);
        }
    };

    useEffect(() => {
        gridMethods.fetchProdutosAsync().then((data) => {
            const formatted = (data || []).map(p => {
                const code = p.codexterno || p.codigo;
                return {
                    ...p,
                    codigo: code,
                    codexterno: code,
                    displayLabel: code ? `${code} - ${p.nome}` : p.nome
                };
            });
            setProdutosList(formatted);
        });
        fetchGroups();
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [empid]);

    const reloadProductsForGroup = async (groupId) => {
        if (!groupId) {
            setListGridProducts(null);
            return;
        }
        setLoading(true);
        try {
            const data = await gridMethods.fetchGestorProductList(groupId);
            setListGridProducts((data || []).map(item => ({
                ...item,
                id: item.prodid,
                isEditing: false
            })));
            setSelectedProducts(null);
        } catch (error) {
            console.error("Erro ao carregar produtos do grupo:", error);
        } finally {
            setLoading(false);
        }
    };

    const ShowProductsList = (e) => {
        const groupId = e.value;
        setSelectedGroup(groupId);
        reloadProductsForGroup(groupId);
    };

    const HandleRowEditComplete = (rowData) => {
        let { newData, index } = rowData;
        if (!newData.prodid) return;

        gridMethods.OnRowEditSave(newData).then(() => {
            let _ListGridProducts = [...ListGridProducts];
            _ListGridProducts[index] = newData;

            const produtoSelecionado = ProdutosList.find((x) => x.id === newData.prodid);
            if (produtoSelecionado) {
                newData.prodNome = produtoSelecionado.nome;
                const code = produtoSelecionado.codexterno || produtoSelecionado.codigo;
                if (code) {
                    newData.codigo = code;
                    newData.codexterno = code;
                }
            }
            setListGridProducts(_ListGridProducts);
            stopEditing(newData);
            gridMethods.app.flashMsg("Item atualizado com sucesso!");
        }).catch((error) => {
            console.error("Erro ao salvar:", error);
        });
    };

    const stopEditing = (rowData) => {
        setEditingRows((prev) => {
            const newEditingRows = { ...prev };
            delete newEditingRows[rowData.id];
            rowData.isEditing = false;
            return newEditingRows;
        });
    };

    const allowEdit = (rowData) => {
        return EditingRows[rowData.id];
    };

    // Abre o diálogo de inclusão com estado limpo
    const handleOpenAddProduct = () => {
        setNewProductData({
            prodid: null,
            qtde: 1,
            periodic: 90
        });
        setIsAddProductDialogVisible(true);
    };

    // Salva o produto vindo do modal
    const handleSaveNewProduct = async (continueAdding = false) => {
        if (!SelectedGroup) {
            gridMethods.app.flashMsg("Selecione um Grupo Gestor antes de adicionar um produto!");
            return;
        }
        if (!newProductData.prodid) {
            gridMethods.app.flashMsg("Por favor, selecione um produto!");
            return;
        }

        setSavingProduct(true);
        const payload = {
            grupoid: SelectedGroup,
            prodid: newProductData.prodid,
            qtde: newProductData.qtde || 1,
            periodic: newProductData.periodic || 90
        };

        const result = await gridMethods.addProdutoAoGrupo(payload);
        setSavingProduct(false);

        if (result.success) {
            await reloadProductsForGroup(SelectedGroup);
            if (continueAdding) {
                setNewProductData({
                    prodid: null,
                    qtde: 1,
                    periodic: 90
                });
            } else {
                setIsAddProductDialogVisible(false);
            }
        }
    };

    async function HandeExcludeSelectedProducts() {
        if (!SelectedProducts || SelectedProducts.length === 0) return;
        const confirmed = await gridMethods.OnClickExcludeSelection(SelectedProducts);
        if (confirmed.success === true) {
            const listIds = SelectedProducts.map(item => item.prodid);
            const newListGridProducts = ListGridProducts.filter(item => !listIds.includes(item.prodid));
            setListGridProducts(newListGridProducts);
            setSelectedProducts(null);
        }
    }

    const handleDeleteSingleProduct = async (prod) => {
        const confirmed = await gridMethods.app.dialog.Confirm("Confirmação", `Deseja remover o produto "${prod.prodNome}" do grupo?`);
        if (confirmed) {
            const singleItemArray = [prod];
            const response = await gridMethods.OnClickExcludeSelection(singleItemArray);
            if (response.success === true) {
                setListGridProducts(prev => prev.filter(item => item.prodid !== prod.prodid));
                if (SelectedProducts) {
                    setSelectedProducts(prev => prev ? prev.filter(item => item.prodid !== prod.prodid) : null);
                }
            }
        }
    };

    const handleClickSelectProductGroup = () => {
        gridMethods.OnClickSelectProductGroup(SelectedGroup, () => {
            reloadProductsForGroup(SelectedGroup);
        });
    };

    const handleDeleteGroup = async () => {
        if (!SelectedGroup) return;
        let response = await gridMethods.OnClickDeleteGroup(SelectedGroup);
        if (response.success === true) {
            const newGroupList = GroupList.filter(item => item.id !== SelectedGroup);
            setGroupList(newGroupList);
            setSelectedGroup(null);
            setListGridProducts(null);
        }
    };

    const handleEditGroup = () => {
        if (!SelectedGroup) return;
        gridMethods.OnClickEditGroup(SelectedGroup, () => {
            fetchRefreshGroups();
        });
    };

    const handleAddGroup = () => {
        gridMethods.OnClickAddGroup(() => {
            fetchRefreshGroups();
        });
    };

    const selectedGroupTitle = GroupList?.find(g => g.id === SelectedGroup)?.title || "";

    // Templates para exibição de produto com código no formato: CODIGO - NOME
    const productOptionTemplate = (option) => {
        if (!option) return null;
        const code = option.codexterno || option.codigo;
        return (
            <div className="flex align-items-center gap-2 py-1">
                {code ? (
                    <span className="font-semibold text-900 font-mono">
                        {code} - {option.nome}
                    </span>
                ) : (
                    <span className="text-900">{option.nome}</span>
                )}
            </div>
        );
    };

    const selectedProductValueTemplate = (option, props) => {
        if (option) {
            const code = option.codexterno || option.codigo;
            return (
                <div className="flex align-items-center gap-2">
                    {code ? (
                        <span className="font-semibold text-900 font-mono">
                            {code} - {option.nome}
                        </span>
                    ) : (
                        <span>{option.nome}</span>
                    )}
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    };

    const addProductDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancelar"
                icon="pi pi-times"
                className="p-button-text p-button-secondary"
                onClick={() => setIsAddProductDialogVisible(false)}
                disabled={savingProduct}
            />
            <Button
                label="Adicionar e Novo"
                icon="pi pi-plus"
                className="p-button-outlined p-button-primary"
                onClick={() => handleSaveNewProduct(true)}
                loading={savingProduct}
                disabled={!newProductData.prodid}
            />
            <Button
                label="Salvar"
                icon="pi pi-check"
                className="p-button-primary"
                onClick={() => handleSaveNewProduct(false)}
                loading={savingProduct}
                disabled={!newProductData.prodid}
            />
        </div>
    );

    return (
        <div className="ghe-container">
            {/* Bloco 1: Seleção e Gerenciamento do Grupo Gestor */}
            <div className="ghe-header-card">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                    <div className="flex-1 w-full md:w-auto">
                        <label className="block text-900 font-semibold mb-2">
                            Grupo Gestor
                        </label>
                        <Dropdown
                            value={SelectedGroup}
                            onChange={ShowProductsList}
                            options={GroupList || []}
                            optionValue="id"
                            optionLabel="title"
                            placeholder="Selecione ou pesquise um Grupo Gestor..."
                            className="w-full"
                            filter
                            filterBy="title"
                            showClear
                            disabled={loading}
                            emptyFilterMessage="Nenhum grupo encontrado"
                            resetFilterOnHide
                        />
                    </div>

                    <div className="flex align-items-center gap-2 pt-0 md:pt-4">
                        <Button
                            type="button"
                            icon="pi pi-refresh"
                            tooltip="Atualizar grupos e produtos"
                            tooltipOptions={{ position: "bottom" }}
                            className="p-button-outlined p-button-secondary"
                            onClick={fetchRefreshGroups}
                            disabled={loading}
                        />

                        <Button
                            type="button"
                            label="Novo Grupo"
                            icon="pi pi-plus"
                            tooltip="Cadastrar um novo Grupo Gestor"
                            tooltipOptions={{ position: "bottom" }}
                            className="p-button-success"
                            onClick={handleAddGroup}
                        />

                        <Button
                            type="button"
                            icon="pi pi-pencil"
                            tooltip="Editar Grupo selecionado"
                            tooltipOptions={{ position: "bottom" }}
                            className="p-button-outlined p-button-info"
                            disabled={!SelectedGroup}
                            onClick={handleEditGroup}
                        />

                        <Button
                            type="button"
                            icon="pi pi-trash"
                            tooltip="Excluir Grupo selecionado"
                            tooltipOptions={{ position: "bottom" }}
                            className="p-button-outlined p-button-danger"
                            disabled={!SelectedGroup}
                            onClick={handleDeleteGroup}
                        />
                    </div>
                </div>
            </div>

            {/* Bloco 2: Gestão de Produtos do Grupo */}
            {SelectedGroup ? (
                <div className="ghe-table-card">
                    {/* Toolbar de Ações de Itens */}
                    <div className="ghe-actions-toolbar">
                        <div className="flex align-items-center gap-2 flex-nowrap">
                            <Button
                                type="button"
                                label="Adicionar Produto"
                                icon="pi pi-box"
                                className="p-button-primary white-space-nowrap"
                                onClick={handleOpenAddProduct}
                            />

                            <Button
                                type="button"
                                label="Importar Agrupamento"
                                icon="pi pi-cart-plus"
                                className="p-button-outlined p-button-secondary white-space-nowrap"
                                tooltip="Vincular produtos de outro agrupamento a este grupo"
                                tooltipOptions={{ position: "bottom" }}
                                onClick={handleClickSelectProductGroup}
                            />
                        </div>

                        {/* A pesquisa ocupa toda a área após IMPORTAR AGRUPAMENTO */}
                        <div className="ghe-search-full">
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" />
                                <InputText
                                    value={globalFilter}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Pesquisar por código ou produto..."
                                    className="p-inputtext-sm w-full"
                                />
                            </span>
                        </div>

                        <div className="flex align-items-center gap-2 flex-nowrap">
                            {SelectedProducts && SelectedProducts.length > 0 && (
                                <Button
                                    type="button"
                                    label={`Excluir Seleção (${SelectedProducts.length})`}
                                    icon="pi pi-trash"
                                    className="p-button-danger white-space-nowrap"
                                    onClick={HandeExcludeSelectedProducts}
                                />
                            )}

                            {ListGridProducts && (
                                <Tag
                                    value={`${ListGridProducts.length} ${ListGridProducts.length === 1 ? 'item' : 'itens'}`}
                                    severity="info"
                                    className="white-space-nowrap"
                                />
                            )}
                        </div>
                    </div>

                    {/* Grade de Produtos */}
                    <CircularWait size="large" loading={loading}>
                        <DataTable
                            ref={dt}
                            value={ListGridProducts || []}
                            selection={SelectedProducts}
                            editMode="row"
                            dataKey="id"
                            selectionMode="checkbox"
                            onSelectionChange={(e) => setSelectedProducts(e.value)}
                            onRowEditComplete={HandleRowEditComplete}
                            tableStyle={{ minWidth: "50rem" }}
                            onRowEditChange={(rowData) => setEditingRows(rowData.data)}
                            editingRows={EditingRows}
                            globalFilter={globalFilter}
                            globalFilterFields={['codigo', 'codexterno', 'prodNome', 'prodid']}
                            emptyMessage={
                                <div className="ghe-empty-state">
                                    <i className="pi pi-inbox" />
                                    <p className="font-semibold text-lg mb-1">Nenhum produto vinculado</p>
                                    <p className="text-sm mb-3">Este grupo gestor ainda não possui produtos associados.</p>
                                    <Button
                                        label="Adicionar Primeiro Produto"
                                        icon="pi pi-plus"
                                        className="p-button-sm p-button-primary"
                                        onClick={handleOpenAddProduct}
                                    />
                                </div>
                            }
                        >
                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>

                            <Column
                                header="Código"
                                field="codigo"
                                body={(rowData) => (
                                    <span className="font-semibold text-700 font-mono">
                                        {rowData.codexterno || rowData.codigo || "-"}
                                    </span>
                                )}
                                style={{ width: "12%", minWidth: "6rem" }}
                                sortable
                            ></Column>

                            <Column
                                header="Produto"
                                field="prodNome"
                                body={(rowData) => (
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-box text-500" />
                                        <span className="font-medium">{rowData.prodNome}</span>
                                    </div>
                                )}
                                editor={(rowData) => gridComponent.edProduto(rowData, ProdutosList)}
                                style={{ width: "45%" }}
                                sortable
                            ></Column>

                            <Column
                                field="qtde"
                                header="Quantidade"
                                editor={gridComponent.edComboQtde}
                                style={{ width: "14%" }}
                                body={(rowData) => (
                                    <span className="font-semibold">{rowData.qtde} un</span>
                                )}
                                sortable
                            ></Column>

                            <Column
                                field="periodic"
                                header="Periodicidade"
                                editor={gridComponent.edPeriodic}
                                style={{ width: "14%" }}
                                body={(rowData) => (
                                    <span>{rowData.periodic} dias</span>
                                )}
                                sortable
                            ></Column>

                            <Column
                                rowEditor={allowEdit}
                                headerStyle={{ width: "12%", minWidth: "6rem" }}
                                bodyStyle={{ textAlign: "center" }}
                                body={(rowData, options) => {
                                    if (EditingRows[rowData.id]) {
                                        return null;
                                    }
                                    return (
                                        <div className="flex justify-content-center gap-1">
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                                tooltip="Remover este produto"
                                                tooltipOptions={{ position: "top" }}
                                                onClick={() => handleDeleteSingleProduct(rowData)}
                                            />
                                        </div>
                                    );
                                }}
                            ></Column>
                        </DataTable>
                    </CircularWait>
                </div>
            ) : (
                <div className="ghe-table-card">
                    <div className="ghe-empty-state">
                        <i className="pi pi-arrow-up" />
                        <h3 className="text-xl font-bold mb-2">Nenhum Grupo Gestor selecionado</h3>
                        <p className="text-secondary max-w-28rem mb-3">
                            Selecione um Grupo Gestor no campo acima para visualizar, adicionar e gerenciar os produtos vinculados.
                        </p>
                    </div>
                </div>
            )}

            {/* Modal Dinâmico: Adicionar Produto ao Grupo */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-box text-primary text-xl" />
                        <span>Adicionar Produto ao Grupo: <strong>{selectedGroupTitle}</strong></span>
                    </div>
                }
                visible={isAddProductDialogVisible}
                style={{ width: "45vw", minWidth: "360px" }}
                footer={addProductDialogFooter}
                onHide={() => setIsAddProductDialogVisible(false)}
                closable={!savingProduct}
            >
                <div className="ghe-dialog-form">
                    <div className="ghe-field">
                        <label htmlFor="new-product-select">
                            Produto <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            id="new-product-select"
                            value={newProductData.prodid}
                            options={ProdutosList}
                            optionLabel="displayLabel"
                            optionValue="id"
                            onChange={(e) => setNewProductData({ ...newProductData, prodid: e.value })}
                            placeholder="Digite código ou nome do produto..."
                            filter
                            filterBy="displayLabel,codexterno,nome,codigo"
                            showClear
                            className="w-full"
                            filterPlaceholder="Pesquisar por código ou nome..."
                            emptyFilterMessage="Nenhum produto encontrado"
                            resetFilterOnHide
                            itemTemplate={productOptionTemplate}
                            valueTemplate={selectedProductValueTemplate}
                            autoFocus
                        />
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6 ghe-field">
                            <label htmlFor="new-product-qtde">Quantidade</label>
                            <InputNumber
                                id="new-product-qtde"
                                value={newProductData.qtde}
                                onValueChange={(e) => setNewProductData({ ...newProductData, qtde: e.value })}
                                min={1}
                                showButtons
                                buttonLayout="horizontal"
                                decrementButtonClassName="p-button-secondary"
                                incrementButtonClassName="p-button-secondary"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                className="w-full"
                            />
                        </div>

                        <div className="col-12 md:col-6 ghe-field">
                            <label htmlFor="new-product-periodic">Periodicidade (dias)</label>
                            <InputNumber
                                id="new-product-periodic"
                                value={newProductData.periodic}
                                onValueChange={(e) => setNewProductData({ ...newProductData, periodic: e.value })}
                                min={1}
                                suffix=" dias"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
